<template>
  <div v-if="show" class="standalone-modal-overlay" @click.self="closeModal">
    <div class="standalone-modal-content" :style="{ maxWidth: '600px' }">
      <!-- Modal Header -->
      <div class="standalone-modal-header">
        <h5 class="modal-title-custom">Fix Error {{ errorIndex + 1 }} of {{ totalErrors }}</h5>
        <button type="button" class="btn-close-custom" @click="closeModal" aria-label="Close" :disabled="isLoading">&times;</button>
      </div>

      <!-- Modal Body -->
      <div class="standalone-modal-body">
        <form @submit.prevent="handleValidate">
          <!-- Product Code (Select with Search) -->
          <div class="form-group-custom mb-3">
            <label for="productCode" class="form-label-custom">Product Code*</label>
            <input 
              type="text" 
              class="form-control-custom mb-2" 
              v-model="productSearchQuery"
              placeholder="Search product by code or name..."
              :disabled="isLoading"
            />
            <select 
              id="productCode" 
              class="form-control-custom" 
              v-model="editableItem.product_code" 
              :disabled="isLoading"
              @change="onProductCodeChange"
              size="5"
            >
              <option disabled value="">Please select a product</option>
              <option 
                v-for="product in filteredPPOBProducts" 
                :key="product.code" 
                :value="product.code"
              >
                {{ product.code }} - {{ product.product_name }}
              </option>
            </select>
          </div>

          <!-- Product Info Box -->
          <div v-if="productInfo" class="product-info-box mb-3">
            <div class="info-row">
              <span class="info-label">Category:</span>
              <span class="info-value">{{ productInfo.category_name }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Price:</span>
              <span class="info-value">IDR {{ formatPrice(productInfo.price) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Admin Fee:</span>
              <span class="info-value">IDR {{ formatPrice(productInfo.admin_fee) }}</span>
            </div>
          </div>

          <!-- Account Number with Check Button -->
          <div class="form-group-custom mb-3">
            <label for="accountNumber" class="form-label-custom">Account Number*</label>
            <div class="input-group-custom">
              <input 
                type="text" 
                id="accountNumber" 
                class="form-control-custom"
                v-model="editableItem.account"
                placeholder="Enter account number"
                :disabled="isLoading"
                @input="onAccountNumberInput"
              />
              <button 
                type="button" 
                class="btn-check-custom" 
                @click="checkAccount" 
                :disabled="isLoading || !editableItem.product_code || !isInquiryRequired"
              >
                Check
              </button>
            </div>
          </div>

          <!-- Account Name (Auto-filled after Check) -->
          <div class="form-group-custom mb-3">
            <label for="accountName" class="form-label-custom">Account Name*</label>
            <input 
              type="text" 
              id="accountName" 
              class="form-control-custom form-control-filled-custom"
              v-model="editableItem.account_name"
              placeholder=""
              readonly
            />
          </div>

          <!-- Server ID (Only show if inquiry required) -->
          <div v-if="isInquiryRequired" class="form-group-custom mb-3">
            <label for="serverId" class="form-label-custom">Server ID*</label>
            <input 
              type="text" 
              id="serverId" 
              class="form-control-custom"
              v-model="editableItem.server_id"
              placeholder="Enter server ID"
              :disabled="isLoading"
            />
          </div>
          
          <div v-if="internalErrorMessage" class="alert-custom alert-danger-custom mb-3" role="alert">
            {{ internalErrorMessage }}
          </div>
           <div v-if="isLoading" class="text-center my-3">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            Validating...
          </div>
        </form>
      </div>

      <!-- Modal Footer -->
      <div class="standalone-modal-footer">
        <button type="button" class="btn-skip-custom" @click="skipError" :disabled="isLoading">Skip</button>
        <button type="button" class="btn-validate-custom" @click="handleValidate" :disabled="isLoading">
          <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          {{ isLoading ? 'Validating...' : 'Validate' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { fixErrorsBatchfileByRow, getPPOBProducts, ppobInquiry } from '@/api/ppob';

const props = defineProps({
  show: Boolean,
  errorItem: Object,
  errorIndex: Number,
  totalErrors: Number,
  batchUUID: String,
});

const emit = defineEmits(['close', 'save-error', 'skip-error', 'validation-start', 'validation-complete']);

const editableItem = ref({});
const internalErrorMessage = ref('');
const isLoading = ref(false);
const productInfo = ref(null);
const ppobProducts = ref([]);
const productSearchQuery = ref('');

// Computed property to filter products based on search query
const filteredPPOBProducts = computed(() => {
  if (!productSearchQuery.value) {
    return ppobProducts.value;
  }
  const query = productSearchQuery.value.toLowerCase();
  return ppobProducts.value.filter(product => 
    product.code.toLowerCase().includes(query) || 
    product.product_name.toLowerCase().includes(query) ||
    (product.category_name && product.category_name.toLowerCase().includes(query))
  );
});

// Computed property to check if inquiry is required for current product
const isInquiryRequired = computed(() => {
  if (!editableItem.value.product_code || !ppobProducts.value.length) return false;
  const product = ppobProducts.value.find(p => p.code === editableItem.value.product_code);
  return product ? product.is_inquiry_required === 1 : false;
});

// Fetch PPOB products immediately when component is created
const fetchPPOBProducts = async () => {
  try {
    const response = await getPPOBProducts();
    if (response && response.status === 'success' && Array.isArray(response.data)) {
      ppobProducts.value = response.data;
    }
  } catch (error) {
    console.error('Error fetching Topup products:', error);
  }
};

// Fetch products immediately
fetchPPOBProducts();

// Also fetch when modal is shown (in case products weren't loaded yet)
watch(() => props.show, (newVal) => {
  if (newVal && ppobProducts.value.length === 0) {
    fetchPPOBProducts();
  }
});

watch(() => props.errorItem, (newItem) => {
  if (newItem) {
    editableItem.value = { 
      product_code: newItem.product_code || '',
      account: newItem.account || '',
      account_name: newItem.account_name || '',
      server_id: newItem.server_id || '',
      uuid: newItem.uuid,
      ...newItem
    };
    internalErrorMessage.value = newItem.error_message || ''; 
    productInfo.value = null;
    
    // If product_code exists, load product info automatically
    if (editableItem.value.product_code) {
      loadProductInfo(editableItem.value.product_code);
    }
  } else {
    editableItem.value = {};
    internalErrorMessage.value = '';
    productInfo.value = null;
  }
}, { immediate: true, deep: true });

const loadProductInfo = (productCode) => {
  if (!ppobProducts.value.length) {
    // If products not loaded yet, try to fetch them
    fetchPPOBProducts().then(() => {
      const product = ppobProducts.value.find(p => p.code === productCode);
      if (product) {
        productInfo.value = {
          product_name: product.product_name,
          category_name: product.category_name,
          price: product.price,
          admin_fee: product.admin_fee,
          is_inquiry_required: product.is_inquiry_required
        };
      }
    });
    return;
  }
  
  const product = ppobProducts.value.find(p => p.code === productCode);
  if (product) {
    productInfo.value = {
      product_name: product.product_name,
      category_name: product.category_name,
      price: product.price,
      admin_fee: product.admin_fee,
      is_inquiry_required: product.is_inquiry_required
    };
  }
};

const onProductCodeChange = () => {
  // Load product info when product code changes
  if (editableItem.value.product_code) {
    loadProductInfo(editableItem.value.product_code);
  } else {
    productInfo.value = null;
  }
  
  // Clear account name when product changes
  editableItem.value.account_name = '';
  internalErrorMessage.value = '';
};

const onAccountNumberInput = () => {
  // If inquiry is NOT required (is_inquiry_required = 0), auto-fill account_name with account number
  if (!isInquiryRequired.value && editableItem.value.account) {
    editableItem.value.account_name = editableItem.value.account;
  }
};

const closeModal = () => {
  if (isLoading.value) return;
  emit('close');
};

const checkAccount = async () => {
  if (!editableItem.value.product_code) {
    internalErrorMessage.value = 'Please select a product code first.';
    return;
  }
  
  if (!editableItem.value.account) {
    internalErrorMessage.value = 'Account number is required to check.';
    return;
  }
  
  // If inquiry required, server_id must be filled before checking
  if (isInquiryRequired.value && !editableItem.value.server_id) {
    internalErrorMessage.value = 'Server ID is required for this product before checking account.';
    return;
  }
  
  isLoading.value = true;
  internalErrorMessage.value = '';
  
  try {
    const formData = new FormData();
    formData.append('product_code', editableItem.value.product_code);
    formData.append('account_number', editableItem.value.account);
    
    // If inquiry required, use inquiry endpoint with server_id
    if (isInquiryRequired.value && editableItem.value.server_id) {
      formData.append('server_id', editableItem.value.server_id);
      
      const response = await ppobInquiry(formData);
      
      if (response && response.status === 'success' && response.data) {
        // Handle different response structures for inquiry
        const accountName = response.data.account_name 
          || response.data.name 
          || response.data.customer_name
          || response.data.accountName
          || '';
        
        editableItem.value.account_name = accountName;
        
        if (!accountName) {
          internalErrorMessage.value = 'Account verified but name not found in response.';
        } else {
          internalErrorMessage.value = '';
        }
      } else {
        editableItem.value.account_name = '';
        internalErrorMessage.value = response.message || (response.data && response.data.message) || 'Failed to verify account details.';
      }
    } else {
      // For non-inquiry products, use regular check endpoint
      const response = await ppobInquiry(formData);

      if (response && response.status === 'success' && response.data) {
        const accountName = response.data.account_name 
          || response.data.name 
          || response.data.customer_name
          || response.data.accountName
          || '';
        
        editableItem.value.account_name = accountName;
        
        if (!accountName) {
          internalErrorMessage.value = 'Account verified but name not found in response.';
        } else {
          internalErrorMessage.value = '';
        }
      } else {
        editableItem.value.account_name = '';
        internalErrorMessage.value = response.message || (response.data && response.data.message) || 'Failed to verify account details.';
      }
    }
  } catch (error) {
    console.error('Error checking account:', error);
    console.error('Error details:', error.response || error);
    editableItem.value.account_name = '';
    
    // Better error handling
    if (error.response && error.response.data) {
      internalErrorMessage.value = error.response.data.message || 'An unexpected error occurred while checking account.';
    } else {
      internalErrorMessage.value = error.message || 'An unexpected error occurred while checking account.';
    }
  } finally {
    isLoading.value = false;
  }
};

const validateLocally = () => {
  if (!editableItem.value.product_code) {
    internalErrorMessage.value = 'Product Code is required.';
    return false;
  }
  
  if (!editableItem.value.account) {
    internalErrorMessage.value = 'Account Number is required.';
    return false;
  }
  
  // For inquiry required products, account_name must be filled via Check button
  // For non-inquiry products, account_name is auto-filled from account number
  if (!editableItem.value.account_name) {
    if (isInquiryRequired.value) {
      internalErrorMessage.value = 'Account Name is required. Please click Check button to verify account.';
    } else {
      internalErrorMessage.value = 'Account Name is required.';
    }
    return false;
  }
  
  // Check if product exists
  const product = ppobProducts.value.find(p => p.code === editableItem.value.product_code);
  if (!product) {
    internalErrorMessage.value = 'Invalid product code. Please select a valid product.';
    return false;
  }
  
  // If inquiry required, server_id must be filled
  if (product.is_inquiry_required === 1 && !editableItem.value.server_id) {
    internalErrorMessage.value = 'Server ID is required for this product.';
    return false;
  }
  
  internalErrorMessage.value = '';
  return true;
};

const handleValidate = async () => {
  if (!validateLocally()) {
    return;
  }
  
  isLoading.value = true;
  emit('validation-start');
  internalErrorMessage.value = '';

  try {
    const formData = new FormData();
    formData.append('uuid', editableItem.value.uuid);
    formData.append('product_code', editableItem.value.product_code);
    formData.append('account', editableItem.value.account);
    
    // server_id only if inquiry required
    if (isInquiryRequired.value && editableItem.value.server_id) {
      formData.append('server_id', editableItem.value.server_id);
    }

    const response = await fixErrorsBatchfileByRow(formData);

    if (response && response.status === 'success') {
      // Get updated price info from the product
      const product = ppobProducts.value.find(p => p.code === editableItem.value.product_code);
      
      emit('save-error', { 
        ...editableItem.value,
        // Update with latest price info from product
        amount: product ? product.price : editableItem.value.amount,
        admin_fee: product ? product.admin_fee : editableItem.value.admin_fee,
        bank: editableItem.value.product_code, // Use product_code as bank for display
        id: editableItem.value.id,
        status: 'Success',
        error_message: null 
      });
    } else {
      internalErrorMessage.value = response.message || (response.data && response.data.message) || 'Validation failed on server.';
      if (response && response.data && response.data.errors && typeof response.data.errors === 'object') {
        const errorMessages = Object.values(response.data.errors).flat().join(', ');
        internalErrorMessage.value = errorMessages || internalErrorMessage.value;
      }
    }
  } catch (error) {
    console.error('Error validating row with API:', error);
    internalErrorMessage.value = error.message || 'An unexpected error occurred during validation.';
  } finally {
    isLoading.value = false;
    emit('validation-complete');
  }
};

const skipError = () => {
  if (isLoading.value) return;
  emit('skip-error', editableItem.value);
};

const formatPrice = (value) => {
  if (value === null || value === undefined) return "0";
  const num = Number(value);
  if (isNaN(num)) return value;
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

</script>

<style scoped>
.standalone-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000; /* Ensure it's on top */
}

.standalone-modal-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 500px; /* Default max-width, can be overridden by prop */
  display: flex;
  flex-direction: column;
}

.standalone-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 15px;
  /* border-bottom: 1px solid #eee; */ /* Optional: if you want a line */
}

.modal-title-custom {
  font-size: 1.5rem; /* Matches image: "Fix Error 1 of 3" */
  font-weight: bold;
  color: #333;
  margin: 0;
}

.btn-close-custom {
  background: none;
  border: none;
  font-size: 1.75rem;
  font-weight: lighter;
  color: #888;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.standalone-modal-body {
  padding-top: 15px;
  padding-bottom: 20px;
}

.form-group-custom {
  margin-bottom: 1rem; /* Consistent spacing */
}

.form-label-custom {
  display: block;
  margin-bottom: .5rem;
  font-size: 0.875rem;
  color: #555;
  font-weight: 500;
}

.form-control-custom, .form-control-custom:disabled {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.375rem; /* Bootstrap-like radius */
  box-sizing: border-box;
  transition: border-color .15s ease-in-out,box-shadow .15s ease-in-out;
}

.form-control-custom:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13,110,253,.25);
}

.form-control-filled-custom {
  background-color: #e9f5e9; /* Light green fill */
  border-color: #a8d8a8;
}

.input-group-custom {
  display: flex;
}

.input-group-custom .form-control-custom {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  flex-grow: 1;
}

.btn-check-custom {
  padding: 0.75rem 1.25rem;
  background-color: #28a745; /* Green */
  color: white;
  border: 1px solid #28a745;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color .15s ease-in-out;
}

.btn-check-custom:hover {
  background-color: #218838;
}

.standalone-modal-footer {
  display: flex;
  justify-content: flex-end; /* Align buttons to the right */
  padding-top: 15px;
  /* border-top: 1px solid #eee; */ /* Optional: if you want a line */
  gap: 10px; /* Space between buttons */
}

.btn-skip-custom, .btn-validate-custom {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  border-radius: 0.375rem;
  cursor: pointer;
  border: 1px solid transparent;
  font-weight: 500;
}

.btn-skip-custom {
  background-color: #f8f9fa; /* Light grey, similar to image */
  color: #333;
  border-color: #ddd;
}

.btn-skip-custom:hover {
  background-color: #e2e6ea;
}

.btn-validate-custom {
  background-color: #007bff; /* Blue */
  color: white;
}

.btn-validate-custom:hover {
  background-color: #0056b3;
}

.alert-custom {
  padding: .75rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
  border-radius: .25rem;
  font-size: 0.9rem;
}

.alert-danger-custom {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #f5c6cb;
}

.product-info-box {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 1rem;
}

.product-info-box .info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.product-info-box .info-row:last-child {
  margin-bottom: 0;
}

.product-info-box .info-label {
  font-weight: 500;
  color: #555;
  font-size: 0.875rem;
}

.product-info-box .info-value {
  color: #333;
  font-size: 0.875rem;
  text-align: right;
}

.btn-check-custom:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.65;
}
</style>